import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Card, LoadingSpinner, Button, Select } from '@tadagram/ui';
import { apiService } from '../../../services/api';
import { ReportAPIResponse, AccountReports, DailyReport } from '../types';
import { ReportList } from './ReportList';
import { ExecutionDetail } from './ExecutionDetail';
import { ChevronLeft, RefreshCw, Calendar as CalendarIcon, BarChart3, FileText, AlertTriangle } from 'lucide-react';

interface ReportDashboardProps {
  activeWorkerId: string | null;
}

export const ReportDashboard: React.FC<ReportDashboardProps> = ({ activeWorkerId }) => {
  const { t } = useTranslation('reports');
  const [searchParams] = useSearchParams();
  const preselectedAccount = searchParams.get('account');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workerUnavailable, setWorkerUnavailable] = useState(false);
  const [workerURL, setWorkerURL] = useState<string | null>(null);
  const [jwtToken, setJwtToken] = useState<string | null>(null);

  const [accounts, setAccounts] = useState<AccountReports[]>([]);
  const [accountNameMap, setAccountNameMap] = useState<Record<string, string>>({});
  const [selectedAccount, setSelectedAccount] = useState<string | null>(
    preselectedAccount || localStorage.getItem('report_selectedAccount')
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(
    localStorage.getItem('report_selectedDate')
  );
  const [dailyReport, setDailyReport] = useState<DailyReport | null>(null);
  const [selectedExecution, setSelectedExecution] = useState<string | null>(
    localStorage.getItem('report_selectedExecution')
  );

  // Persist report navigation state → survive page reload
  useEffect(() => {
    if (selectedAccount) localStorage.setItem('report_selectedAccount', selectedAccount);
    else localStorage.removeItem('report_selectedAccount');
  }, [selectedAccount]);

  useEffect(() => {
    if (selectedDate) localStorage.setItem('report_selectedDate', selectedDate);
    else localStorage.removeItem('report_selectedDate');
  }, [selectedDate]);

  useEffect(() => {
    if (selectedExecution) localStorage.setItem('report_selectedExecution', selectedExecution);
    else localStorage.removeItem('report_selectedExecution');
  }, [selectedExecution]);

  const isWorkerUnavailableError = (err: any): boolean => {
    const message = String(err?.message || '');
    return err?.status === 503 || err?.isWorkerUnavailable === true || /503|service unavailable/i.test(message);
  };

  // Fetch account names from my-accounts API
  useEffect(() => {
    (async () => {
      try {
        const response = await apiService.getAccounts();
        const accountsList = response?.data?.accounts || response?.accounts || [];
        const nameMap: Record<string, string> = {};
        for (const acc of accountsList) {
          const id = acc._id || acc.id;
          if (id) {
            nameMap[id] = acc.full_name || acc.fullName || acc.name || acc.username || acc.email || id;
          }
        }
        setAccountNameMap(nameMap);
      } catch (err) {
        console.error('Failed to fetch account names:', err);
      }
    })();
  }, []);

  // Fetch worker URL and reports list
  const fetchReportsData = useCallback(async () => {
    if (!activeWorkerId) return;

    setLoading(true);
    setError(null);
    setWorkerUnavailable(false);

    try {
      const urlResponse: ReportAPIResponse = await apiService.getWorkerReportURL(activeWorkerId);
      const { worker, jwt_token } = urlResponse;

      setWorkerURL(worker.url);
      setJwtToken(jwt_token || '');

      const reportsData = await apiService.getReportsList(worker.url, jwt_token || '');
      setAccounts(reportsData.accounts || []);

      if (reportsData.accounts && reportsData.accounts.length > 0) {
        // Keep saved account if it still exists in the list, otherwise pick first
        const savedAccount = localStorage.getItem('report_selectedAccount');
        const hasSaved = savedAccount && reportsData.accounts.some((a: AccountReports) => a.account_id === savedAccount);
        const hasPreselected = preselectedAccount && reportsData.accounts.some((a: AccountReports) => a.account_id === preselectedAccount);
        if (!hasPreselected && !hasSaved) {
          setSelectedAccount(reportsData.accounts[0].account_id);
        }
      }
    } catch (err: any) {
      console.error('Failed to fetch reports data:', err);
      if (isWorkerUnavailableError(err)) {
        setWorkerUnavailable(true);
        setError('Worker service unavailable (503)');
      } else {
        setError(err.message || 'Failed to load reports data');
      }
    } finally {
      setLoading(false);
    }
  }, [activeWorkerId]);

  useEffect(() => {
    if (activeWorkerId) {
      fetchReportsData();
    }
  }, [activeWorkerId, fetchReportsData]);



  const fetchDailyReport = useCallback(async (accountId: string, date: string) => {
    if (!workerURL || !jwtToken) return;

    setLoading(true);
    setError(null);
    setWorkerUnavailable(false);

    try {
      const report = await apiService.getDailyReport(workerURL, jwtToken, accountId, date);
      setDailyReport(report);
      setSelectedDate(date);
    } catch (err: any) {
      console.error('Failed to fetch daily report:', err);
      if (isWorkerUnavailableError(err)) {
        setWorkerUnavailable(true);
        setError('Worker service unavailable (503)');
      } else {
        setError(err.message || 'Failed to load daily report');
      }
    } finally {
      setLoading(false);
    }
  }, [workerURL, jwtToken]);

  // Restore daily report after reload — placed AFTER fetchDailyReport is defined
  useEffect(() => {
    if (workerURL && jwtToken && selectedAccount && selectedDate && !dailyReport) {
      fetchDailyReport(selectedAccount, selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workerURL, jwtToken]);

  const handleAccountChange = (accountId: string) => {
    setSelectedAccount(accountId);
    setSelectedDate(null);
    setDailyReport(null);
    setSelectedExecution(null);
  };

  const handleDateSelect = (accountId: string, date: string) => {
    fetchDailyReport(accountId, date);
    setSelectedExecution(null);
  };

  const handleBackToList = () => {
    if (selectedExecution) {
      setSelectedExecution(null);
    } else if (selectedDate) {
      setSelectedDate(null);
      setDailyReport(null);
    }
  };

  const handleRefresh = () => {
    if (selectedDate && selectedAccount) {
      fetchDailyReport(selectedAccount, selectedDate);
    } else {
      fetchReportsData();
    }
  };

  if (!activeWorkerId) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{
          textAlign: 'center',
          padding: '64px 24px',
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          borderRadius: '16px',
          border: '1px dashed #93c5fd',
        }}>
          <BarChart3 size={48} style={{ color: '#93c5fd', marginBottom: '16px' }} />
          <p style={{ color: '#64748b', fontSize: '15px' }}>Select a worker to view execution reports</p>
        </div>
      </div>
    );
  }

  if (loading || (selectedDate && !dailyReport)) {
    return (
      <div style={{ padding: '24px' }}>
        <LoadingSpinner text={t('loading')} />
      </div>
    );
  }

  if (workerUnavailable) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{
          padding: '24px',
          background: 'linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)',
          borderRadius: '16px',
          border: '1px solid #fed7aa',
          textAlign: 'center',
        }}>
          <AlertTriangle size={40} style={{ color: '#f59e0b', marginBottom: '12px' }} />
          <p style={{ color: '#92400e', fontWeight: 500, marginBottom: '12px' }}>
            {t('workerUnavailableDesc')}
          </p>
          <button
            onClick={fetchReportsData}
            style={{
              padding: '8px 20px',
              background: '#f59e0b',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '13px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <RefreshCw size={14} /> {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{
          padding: '24px',
          background: 'linear-gradient(135deg, #fef2f2 0%, #fff1f2 100%)',
          borderRadius: '16px',
          border: '1px solid #fecaca',
          textAlign: 'center',
        }}>
          <AlertTriangle size={40} style={{ color: '#f87171', marginBottom: '12px' }} />
          <p style={{ color: '#dc2626', fontWeight: 500, marginBottom: '12px' }}>{error}</p>
          <button
            onClick={fetchReportsData}
            style={{
              padding: '8px 20px',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '13px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <RefreshCw size={14} /> {t('retry')}
          </button>
        </div>
      </div>
    );
  }

  const selectedAccountData = accounts.find(a => a.account_id === selectedAccount);
  const selectedExecutionData = dailyReport?.executions.find(e => e.execution_id === selectedExecution);

  return (
    <div style={{ padding: '20px 12px' }}>
      {/* ── Header ─────────────────────────────── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px',
      }}>
        {(selectedDate || selectedExecution) && (
          <button
            onClick={handleBackToList}
            style={{
              width: '36px', height: '36px',
              borderRadius: '10px',
              border: '1px solid #e2e8f0',
              background: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
          >
            <ChevronLeft size={18} style={{ color: '#475569' }} />
          </button>
        )}

        <div style={{ flex: 1 }}>
          <h1 style={{
            margin: 0,
            fontSize: '20px',
            fontWeight: 700,
            color: selectedExecution ? '#7c3aed' : selectedDate ? '#2563eb' : '#0f172a',
          }}>
            {selectedExecution ? 'Execution Details' : selectedDate ? `Report — ${selectedDate}` : 'Execution Reports'}
          </h1>
          {!selectedExecution && !selectedDate && selectedAccountData && (
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#94a3b8' }}>
              {selectedAccountData.date_range.start} to {selectedAccountData.date_range.end}
            </p>
          )}
        </div>

        <button
          onClick={handleRefresh}
          disabled={loading}
          style={{
            width: '36px', height: '36px',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            background: 'white',
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: loading ? 0.5 : 1,
            transition: 'all 0.15s',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          }}
        >
          <RefreshCw size={16} style={{ color: '#64748b' }} className={loading ? 'rotating' : ''} />
        </button>
      </div>

      {/* ── Account Selector ─────────────────── */}
      {!selectedExecution && accounts.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <Select
            value={selectedAccount || ''}
            onChange={(e) => handleAccountChange(e.target.value)}
            options={[
              { value: '', label: t('selectAccount') },
              ...accounts.map(account => ({
                value: account.account_id,
                label: `${accountNameMap[account.account_id] || `Account ${account.account_id.slice(0, 8)}...`} (${account.total_reports} reports)`
              }))
            ]}
            style={{ width: '100%' }}
          />
        </div>
      )}

      {/* ── Content ──────────────────────────── */}
      {selectedExecution && selectedExecutionData ? (
        <ExecutionDetail execution={selectedExecutionData} accountNameMap={accountNameMap} />
      ) : selectedDate && dailyReport ? (
        <ReportList
          report={dailyReport}
          onExecutionSelect={(executionId) => setSelectedExecution(executionId)}
        />
      ) : selectedAccount && selectedAccountData ? (
        <div style={{ display: 'grid', gap: '10px' }}>
          {[...selectedAccountData.reports].reverse().map((date, idx) => {
            const isToday = date === new Date().toISOString().slice(0, 10);
            return (
              <button
                key={date}
                onClick={() => handleDateSelect(selectedAccount, date)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '14px 18px',
                  background: isToday
                    ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
                    : 'white',
                  border: isToday ? '1px solid #93c5fd' : '1px solid #e2e8f0',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  width: '100%',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.04)';
                }}
              >
                <div style={{
                  width: '42px', height: '42px',
                  borderRadius: '12px',
                  background: isToday
                    ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                    : 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <CalendarIcon size={20} style={{ color: isToday ? '#fff' : '#64748b' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontWeight: 600,
                    fontSize: '15px',
                    color: '#1e293b',
                  }}>
                    {date}
                  </div>
                  {isToday && (
                    <span style={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#2563eb',
                      background: '#dbeafe',
                      padding: '1px 8px',
                      borderRadius: '4px',
                      marginTop: '2px',
                      display: 'inline-block',
                    }}>
                      Today
                    </span>
                  )}
                </div>
                <ChevronLeft size={18} style={{ color: '#94a3b8', transform: 'rotate(180deg)' }} />
              </button>
            );
          })}
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '64px 24px',
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
          borderRadius: '16px',
          border: '1px dashed #93c5fd',
        }}>
          <FileText size={48} style={{ color: '#93c5fd', marginBottom: '16px' }} />
          <p style={{ color: '#64748b', fontSize: '15px' }}>
            {accounts.length === 0 ? t('noReports') : t('noAccount')}
          </p>
        </div>
      )}
    </div>
  );
};
